import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import {
  GraphConditionId,
  GraphConditionRequestParams,
  GraphConditionResponse,
  GraphConditionTaskRequestParams,
  SortConditionTaskQuery,
} from '@business/entities/graph_condition';
import { GraphConditionAPI } from '@business/api/graph_condition';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { CompactGraphResponse } from '@business/entities/graph';
import { TaskId } from '@business/entities/battle_plan';

export class GraphConditionBloc extends BaseBloc {
  private readonly graphConditionRepo = new Repository<GraphConditionResponse>({
    getItemId: item => item.id,
  });

  readonly allConditions$ = this.graphConditionRepo.items$;
  readonly conditions = this.graphConditionRepo.items;
  readonly selectCondition = this.graphConditionRepo.selectItem;

  onReset() {
    this.graphConditionRepo.reset();
  }

  createCondition = (data: Partial<GraphConditionResponse>) => {
    return GraphConditionAPI.createGraphCondition(data).pipe(
      this.graphConditionRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_condition_graph'),
      }),
    );
  };

  updateCondition = (id: GraphConditionId, data: Partial<GraphConditionResponse>) => {
    return GraphConditionAPI.updateGraphCondition(id, data).pipe(
      this.graphConditionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_condition_graph'),
      }),
    );
  };

  fetchConditions = (params?: GraphConditionRequestParams) => {
    return GraphConditionAPI.fetchGraphConditions(params).pipe(
      this.graphConditionRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_graph_error') }),
    );
  };

  fetchCondition = (id: GraphConditionId) => {
    return GraphConditionAPI.fetchGraphCondition(id).pipe(
      this.graphConditionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_condition_graph_group_error') }),
    );
  };

  createTask = (id: GraphConditionId, params: GraphConditionTaskRequestParams) => {
    return GraphConditionAPI.createTask(id, params).pipe(
      this.graphConditionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_condition_graph_task_sort'),
      }),
    );
  };

  updateConditionValue = (id: GraphConditionId, data: Partial<GraphConditionResponse>) => {
    return GraphConditionAPI.updateConditionValue(id, data).pipe(
      this.graphConditionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_condition_graph_task_sort'),
      }),
    );
  };

  sortTasks = (id: GraphConditionId, data: SortConditionTaskQuery) => {
    return GraphConditionAPI.sortTasks(id, data).pipe(
      this.graphConditionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_condition_graph_group_error') }),
    );
  };

  deleteCondition = (id: GraphConditionId) => {
    return showDeleteConfirmation(`Delete Codition`, `Do you really want to remove this Graph Condition?`).pipe(
      switchMap(() => GraphConditionAPI.deleteGraphCondition(id)),
      //   this.orgBoardRepo.ops.removeOne(() => id),
      this.graphConditionRepo.ops.reset(),
      handleMessage({
        type: requestMessage('delete_programs'),
      }),
    );
  };

  deleteTask = (id: TaskId) => {
    return showDeleteConfirmation(`Delete Task`, `Do you really want to remove this Task?`).pipe(
      switchMap(() => GraphConditionAPI.deleteTask(id)),
      this.graphConditionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_programs'),
      }),
    );
  };

  fetchGraphs = () => {
    return GraphConditionAPI.fetchGraphs().pipe(handleMessage({ error: requestMessage('fetch_graph_error') }));
  };
}
